import { supabase } from '../lib/supabase';
import { logger } from './loggingService';
import { toast } from '../lib/toast';
import { subscriptionManager } from './subscriptionManager';
import { APIClient } from './api/apiClient';
import { HealthCheckService } from './healthCheckService';

// Graceful synchronous loading for missing services during development
let NotificationService: any = null;
let ErrorHandlerService: any = null;

// Try to load NotificationService synchronously
try {
  // This will fail during build if file doesn't exist, but we catch it
  const notificationModule = require('./notificationService');
  NotificationService = notificationModule.NotificationService || notificationModule.default || notificationModule;
} catch (error) {
  logger.warn('NotificationService not available - using fallback', { error: (error as Error).message });
  // Fallback notification handling using toast directly
  NotificationService = {
    initialize: async () => logger.info('Fallback notification service initialized'),
    cleanup: async () => {},
    addNotification: (message: string, type: 'info' | 'success' | 'warning' | 'error' = 'info') => {
      const toastType = type === 'error' ? 'error' : type === 'success' ? 'success' : type === 'warning' ? 'warning' : 'info';
      logger[toastType](message);
      if (typeof toast !== 'undefined') {
        toast[toastType](message);
      }
    },
    getNotifications: () => [],
    clearNotifications: () => {},
    clearNotification: () => {}
  };
}

// Try to load ErrorHandlerService synchronously
try {
  const errorModule = require('./errorHandler');
  ErrorHandlerService = errorModule.ErrorHandlerService || errorModule.default || errorModule;
} catch (error) {
  logger.warn('ErrorHandlerService not available - using fallback', { error: (error as Error).message });
  // Fallback error handling
  ErrorHandlerService = {
    initialize: () => {
      logger.info('Fallback error handler initialized');
      if (typeof window !== 'undefined') {
        window.addEventListener('error', (event: ErrorEvent) => {
          logger.error('Global error caught (fallback)', { 
            message: event.message, 
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno 
          });
        });

        window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
          logger.error('Unhandled promise rejection (fallback)', { 
            reason: event.reason 
          });
          event.preventDefault();
        });
      }
    },
    cleanup: () => {},
    handleError: (error: Error, context?: Record<string, any>) => {
      logger.error('Error handled by fallback handler', { 
        error: error.message,
        stack: error.stack,
        context 
      });
      if (typeof toast !== 'undefined' && context?.showToast !== false) {
        toast.error(error.message || 'An unexpected error occurred');
      }
    }
  };
}

interface ServiceConfig {
  name: string;
  initialize: () => Promise<void>;
  cleanup?: () => Promise<void>;
  dependencies?: string[];
  required?: boolean;
  retryOnFailure?: boolean;
  healthCheck?: () => Promise<boolean>;
  priority?: number;
}

interface ServiceStatus {
  name: string;
  initialized: boolean;
  error?: Error;
  startTime?: Date;
  endTime?: Date;
  duration?: number;
  healthy?: boolean;
}

/**
 * Manages application service initialization and lifecycle with robust error handling
 */
export class ServiceInitializer {
  private static instance: ServiceInitializer | null = null;
  private services: Map<string, ServiceConfig> = new Map();
  private statuses: Map<string, ServiceStatus> = new Map();
  private isInitializing = false;
  private isInitialized = false;
  private healthCheckTimer: NodeJS.Timeout | null = null;
  private shutdownHandlers: Array<() => Promise<void>> = [];

  private constructor() {
    this.setupShutdownHandlers();
    this.registerCoreServices();
  }

  /**
   * Get singleton instance
   */
  static getInstance(): ServiceInitializer {
    if (!this.instance) {
      this.instance = new ServiceInitializer();
    }
    return this.instance;
  }

  /**
   * Register core services with graceful error handling
   */
  private registerCoreServices(): void {
    // 1. Error Handler (highest priority, no dependencies)
    this.registerService({
      name: 'errorHandler',
      priority: 100,
      required: true,
      initialize: async () => {
        if (ErrorHandlerService && typeof ErrorHandlerService.initialize === 'function') {
          ErrorHandlerService.initialize();
          logger.info('ErrorHandlerService initialized');
        } else {
          logger.warn('ErrorHandlerService unavailable - using basic error handling');
          // Basic fallback error handling
          if (typeof window !== 'undefined') {
            window.addEventListener('error', (event: ErrorEvent) => {
              logger.error('Global error caught (basic)', { 
                message: event.message, 
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno 
              });
            });

            window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
              logger.error('Unhandled promise rejection (basic)', { 
                reason: event.reason 
              });
              event.preventDefault();
            });
          }
        }
      },
      cleanup: async () => {
        if (ErrorHandlerService && typeof ErrorHandlerService.cleanup === 'function') {
          ErrorHandlerService.cleanup();
        }
      }
    });

    // 2. Authentication (always required)
    this.registerService({
      name: 'auth',
      priority: 90,
      required: true,
      dependencies: ['errorHandler'],
      initialize: async () => {
        try {
          const { data: { session }, error } = await supabase.auth.getSession();
          
          if (error) {
            logger.error('Session check failed during auth service initialization', { error: error.message });
            throw new Error(`Failed to get session: ${error.message}`);
          }
          
          if (!session) {
            // No session - this should not happen if user exists (AuthContext should handle this)
            // But if it does, we should fail initialization so user gets redirected to login
            logger.warn('No active session found during auth service initialization');
            throw new Error('No active session - user should be redirected to login');
          }
          
          // Setup auth state change listener
          const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            logger.info('Auth state changed', { event, hasSession: !!session });
            
            if (event === 'SIGNED_IN' && session) {
              logger.info('User signed in', { 
                userId: session.user.id,
                email: session.user.email 
              });
            } else if (event === 'SIGNED_OUT') {
              this.handleSignOut();
            } else if (event === 'TOKEN_REFRESHED') {
              logger.info('Token refreshed automatically');
            } else if (event === 'USER_UPDATED') {
              logger.info('User profile updated');
            }
          });

          logger.info('Authentication service initialized', { 
            userId: session.user.id,
            email: session.user.email,
            expiresAt: new Date(session.expires_at! * 1000).toISOString()
          });
        } catch (error) {
          logger.error('Authentication initialization failed', { error });
          // Required service - fail hard so user gets redirected to login
          throw error;
        }
      },
      healthCheck: async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          return !!session;
        } catch (error) {
          logger.warn('Auth health check failed', { error });
          return false;
        }
      }
    });

    // 3. Notification Service (optional but useful)
    this.registerService({
      name: 'notifications',
      priority: 85,
      required: false,
      dependencies: ['auth'],
      initialize: async () => {
        if (NotificationService && typeof NotificationService.initialize === 'function') {
          await NotificationService.initialize();
          logger.info('NotificationService initialized');
        } else {
          logger.info('NotificationService unavailable - using toast fallback');
          // Fallback: toast is already available globally
        }
      },
      cleanup: async () => {
        if (NotificationService && typeof NotificationService.cleanup === 'function') {
          await NotificationService.cleanup();
        }
      }
    });

    // 4. Real-time Subscriptions (optional)
    this.registerService({
      name: 'subscriptions',
      priority: 80,
      required: false,
      dependencies: ['auth'],
      initialize: async () => {
        // Subscriptions are created on-demand by components
        // Just verify the manager is ready
        logger.info('Real-time subscription manager ready');
      },
      cleanup: async () => {
        try {
          await subscriptionManager.unsubscribeAll();
          logger.debug('All subscriptions cleaned up');
        } catch (error) {
          logger.warn('Subscription cleanup failed', { error });
        }
      },
      healthCheck: async () => {
        try {
          const stats = subscriptionManager.getStats();
          return stats.errors === 0 && stats.total >= 0;
        } catch (error) {
          logger.warn('Subscription health check failed', { error });
          return false;
        }
      }
    });

    // 5. Health Monitoring (optional)
    this.registerService({
      name: 'healthCheck',
      priority: 70,
      required: false,
      dependencies: ['auth'],
      initialize: async () => {
        if (typeof HealthCheckService !== 'undefined' && HealthCheckService && typeof HealthCheckService.initialize === 'function') {
          await HealthCheckService.initialize();
          if (typeof HealthCheckService.startPeriodicChecks === 'function') {
            HealthCheckService.startPeriodicChecks(300000); // 5 minutes
          }
          logger.info('Health check service initialized');
        } else {
          logger.warn('HealthCheckService unavailable - skipping advanced health monitoring');
        }
      },
      cleanup: async () => {
        if (typeof HealthCheckService !== 'undefined' && HealthCheckService) {
          if (typeof HealthCheckService.stopPeriodicChecks === 'function') {
            HealthCheckService.stopPeriodicChecks();
          }
        }
      },
      healthCheck: async () => {
        if (typeof HealthCheckService !== 'undefined' && HealthCheckService && typeof HealthCheckService.checkHealth === 'function') {
          return await HealthCheckService.checkHealth();
        }
        return true; // Assume healthy if service not available
      }
    });

    // 6. EventBus (optional, for pub/sub)
    this.registerService({
      name: 'eventBus',
      priority: 65,
      required: false,
      dependencies: ['auth'],
      initialize: async () => {
        try {
          const { EventBus } = await import('./EventBus');
          await EventBus.initialize();
          logger.info('EventBus initialized');
        } catch (error) {
          logger.warn('EventBus unavailable', { error });
        }
      },
      cleanup: async () => {
        try {
          const { EventBus } = await import('./EventBus');
          await EventBus.cleanup();
        } catch (error) {
          logger.warn('EventBus cleanup failed', { error });
        }
      }
    });

    // 7. SchedulerService (optional, for cron jobs)
    this.registerService({
      name: 'scheduler',
      priority: 60,
      required: false,
      dependencies: ['auth'],
      initialize: async () => {
        try {
          const { SchedulerService } = await import('./SchedulerService');
          // Start scheduler in Node.js environment (for cron jobs)
          if (typeof process !== 'undefined' && process.env) {
            SchedulerService.startScheduler();
            logger.info('SchedulerService initialized and started');
          } else {
            logger.info('SchedulerService initialized (browser environment - will start on demand)');
          }
        } catch (error) {
          logger.warn('SchedulerService unavailable', { error });
        }
      },
      cleanup: async () => {
        try {
          const { SchedulerService } = await import('./SchedulerService');
          SchedulerService.stopScheduler();
        } catch (error) {
          logger.warn('SchedulerService cleanup failed', { error });
        }
      }
    });

    // 6. Cache Management (optional)
    this.registerService({
      name: 'cache',
      priority: 60,
      required: false,
      initialize: async () => {
        // Setup cache cleanup timer with error handling
        const cleanupTimer = setInterval(() => {
          try {
            if (typeof APIClient.getCacheStats === 'function') {
              const stats = APIClient.getCacheStats();
              logger.debug('Cache maintenance running', { 
                size: stats.size,
                entries: stats.entries.length 
              });
              
              // Clear old entries
              stats.entries.forEach(entry => {
                if (entry.age > entry.ttl) {
                  try {
                    APIClient.clearCache(entry.key);
                  } catch (clearError) {
                    logger.warn('Failed to clear cache entry', { key: entry.key, error: clearError });
                  }
                }
              });
            }
          } catch (error) {
            logger.warn('Cache maintenance failed', { error: (error as Error).message });
          }
        }, 60000); // Check every minute

        // Store timer reference for cleanup
        (this as any).cacheCleanupTimer = cleanupTimer;
        
        logger.info('Cache management initialized');
      },
      cleanup: async () => {
        try {
          if (typeof APIClient.clearCache === 'function') {
            APIClient.clearCache();
            logger.debug('API cache cleared');
          }
          
          // Clear cleanup timer
          if ((this as any).cacheCleanupTimer) {
            clearInterval((this as any).cacheCleanupTimer);
            delete (this as any).cacheCleanupTimer;
            logger.debug('Cache cleanup timer stopped');
          }
        } catch (error) {
          logger.warn('Cache cleanup failed', { error: (error as Error).message });
        }
      }
    });

    // 7. Performance Monitoring (optional, browser-only)
    this.registerService({
      name: 'performance',
      priority: 50,
      required: false,
      initialize: async () => {
        if (typeof window !== 'undefined' && 'performance' in window && 'PerformanceObserver' in window) {
          try {
            const observer = new PerformanceObserver((list) => {
              try {
                for (const entry of list.getEntries()) {
                  if (entry.entryType === 'navigation') {
                    const navEntry = entry as PerformanceNavigationTiming;
                    logger.info('Page load performance metrics', {
                      domContentLoaded: navEntry.domContentLoadedEventEnd - navEntry.domContentLoadedEventStart,
                      loadComplete: navEntry.loadEventEnd - navEntry.loadEventStart,
                      totalTime: navEntry.loadEventEnd - navEntry.fetchStart,
                      transferSize: navEntry.transferSize,
                      decodedBodySize: navEntry.decodedBodySize
                    });
                  } else if (entry.entryType === 'resource') {
                    // Log slow resources
                    const resourceEntry = entry as PerformanceResourceTiming;
                    if (resourceEntry.responseEnd - resourceEntry.fetchStart > 2000) {
                      logger.warn('Slow resource loaded', {
                        name: resourceEntry.name,
                        duration: resourceEntry.responseEnd - resourceEntry.fetchStart,
                        initiatorType: resourceEntry.initiatorType
                      });
                    }
                  }
                }
              } catch (perfError) {
                logger.warn('Performance observer error', { error: (perfError as Error).message });
              }
            });
            
            observer.observe({ 
              entryTypes: ['navigation', 'resource', 'paint', 'largest-contentful-paint'] 
            });
            
            logger.info('Performance monitoring initialized');
          } catch (error) {
            logger.warn('Performance monitoring setup failed', { error: (error as Error).message });
          }
        } else {
          logger.info('Performance monitoring not available in this environment');
        }
      }
    });
  }

  /**
   * Register a service with error resilience and validation
   */
  registerService(config: ServiceConfig): void {
    // Validate config
    if (!config.name || typeof config.name !== 'string') {
      logger.error('Invalid service configuration - missing name');
      return;
    }

    if (this.services.has(config.name)) {
      logger.warn('Service already registered, skipping duplicate', { name: config.name });
      return;
    }

    // Validate initialize function
    if (!config.initialize || typeof config.initialize !== 'function') {
      logger.error('Invalid service configuration - missing initialize function', { name: config.name });
      return;
    }

    const validatedConfig: ServiceConfig = {
      priority: 0,
      required: false,
      retryOnFailure: true,
      ...config
    };

    this.services.set(config.name, validatedConfig);

    this.statuses.set(config.name, {
      name: config.name,
      initialized: false
    });

    logger.debug('Service registered successfully', { 
      name: config.name, 
      priority: validatedConfig.priority,
      required: validatedConfig.required 
    });
  }

  /**
   * Initialize all services with robust error handling and isolation
   */
  async initialize(): Promise<boolean> {
    if (this.isInitialized) {
      logger.warn('Services already initialized, skipping');
      return true;
    }

    if (this.isInitializing) {
      logger.warn('Initialization already in progress, waiting...');
      // Wait for current initialization to complete
      while (this.isInitializing) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      return this.isInitialized;
    }

    this.isInitializing = true;
    logger.info('🚀 Starting service initialization sequence');

    const startTime = Date.now();
    let criticalFailures = 0;
    const initializationResults: Array<{ name: string; success: boolean; error?: string; duration: number }> = [];

    try {
      // Sort services by priority and dependencies
      const sortedServices = this.sortServices();
      logger.debug('Service initialization order determined', { 
        order: sortedServices.map(s => s.name),
        total: sortedServices.length 
      });

      // Initialize services in order with comprehensive error isolation
      for (const service of sortedServices) {
        const serviceStart = Date.now();
        let serviceSuccess = false;
        let serviceError: string | undefined;

        try {
          await this.initializeService(service);
          serviceSuccess = true;
          logger.info(`✅ ${service.name} initialized successfully`);
        } catch (error) {
          serviceError = error instanceof Error ? error.message : String(error);
          logger.error(`❌ ${service.name} initialization failed`, { 
            error: serviceError,
            duration: Date.now() - serviceStart 
          });

          // Track critical failures
          if (service.required) {
            criticalFailures++;
          }
          
          // Don't let one service completely block others unless it's a critical dependency
          if (service.required && criticalFailures > 1) {
            logger.error('Multiple critical service failures - aborting initialization');
            break;
          }
        } finally {
          const duration = Date.now() - serviceStart;
          initializationResults.push({
            name: service.name,
            success: serviceSuccess,
            error: serviceError,
            duration
          });

          this.statuses.set(service.name, {
            ...this.statuses.get(service.name)!,
            duration
          });
        }
      }

      // Finalize initialization
      this.isInitialized = criticalFailures === 0;
      this.isInitializing = false;

      const totalDuration = Date.now() - startTime;
      const successful = initializationResults.filter(r => r.success).length;
      
      logger.info('🎯 Service initialization sequence completed', {
        totalDuration,
        successful,
        failed: initializationResults.length - successful,
        criticalFailures,
        services: initializationResults
      });

      // Start health monitoring only if we have at least some services running
      if (successful > 0) {
        this.startHealthMonitoring();
      }

      if (criticalFailures > 0) {
        toast.error(`Application started with ${criticalFailures} critical service failures. Some features may be limited.`);
        return false;
      }

      return true;
    } catch (error) {
      this.isInitializing = false;
      const finalError = error instanceof Error ? error.message : String(error);
      logger.error('💥 Critical initialization error', { error: finalError });
      
      toast.error('Failed to initialize application. Please refresh the page.');
      
      // Emergency cleanup
      await this.cleanup();
      
      return false;
    }
  }

  /**
   * Initialize a single service with comprehensive error handling
   */
  private async initializeService(config: ServiceConfig): Promise<void> {
    const status = this.statuses.get(config.name)!;
    
    if (status.initialized) {
      logger.debug(`${config.name} already initialized, skipping`);
      return;
    }

    // Validate service configuration
    if (!config.initialize || typeof config.initialize !== 'function') {
      throw new Error(`Invalid service configuration for ${config.name}: missing initialize function`);
    }

    // Check dependencies with detailed logging
    if (config.dependencies && config.dependencies.length > 0) {
      for (const dep of config.dependencies) {
        const depStatus = this.statuses.get(dep);
        if (!depStatus) {
          const error = new Error(`Missing dependency service: ${dep} for ${config.name}`);
          if (config.required) {
            throw error;
          } else {
            logger.warn(`Non-critical dependency missing: ${dep} for ${config.name}`);
            status.error = error;
            return;
          }
        }
        
        if (!depStatus.initialized) {
          const error = new Error(`Dependency ${dep} not initialized for ${config.name}`);
          if (config.required) {
            throw error;
          } else {
            logger.warn(`Skipping ${config.name} due to uninitialized dependency ${dep}`);
            status.error = error;
            return;
          }
        }
        
        logger.debug(`Dependency check passed: ${dep} for ${config.name}`);
      }
    }

    status.startTime = new Date();
    
    try {
      logger.info(`🔄 Initializing ${config.name}...`, { priority: config.priority, required: config.required });
      
      // Execute initialization with timeout protection
      const initializationPromise = config.initialize();
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error(`Service ${config.name} initialization timeout (30s)`)), 30000)
      );
      
      await Promise.race([initializationPromise, timeoutPromise]);
      
      status.initialized = true;
      status.endTime = new Date();
      status.duration = status.endTime.getTime() - status.startTime!.getTime();
      status.healthy = true;
      
      logger.info(`✅ ${config.name} initialized successfully`, { 
        duration: `${status.duration}ms`,
        required: config.required 
      });
    } catch (error) {
      const serviceError = error instanceof Error ? error : new Error(String(error));
      status.error = serviceError;
      status.endTime = new Date();
      status.duration = status.endTime.getTime() - status.startTime!.getTime();
      status.healthy = false;
      
      logger.error(`❌ Failed to initialize ${config.name}`, { 
        error: serviceError.message,
        stack: serviceError.stack,
        duration: `${status.duration}ms`,
        required: config.required,
        retryOnFailure: config.retryOnFailure
      });

      // Handle required vs optional services differently
      if (config.required) {
        // Critical failure - throw to stop initialization
        throw new Error(`Required service ${config.name} failed to initialize: ${serviceError.message}`);
      } else if (config.retryOnFailure) {
        // Schedule retry for non-critical services
        logger.info(`⏳ Scheduling retry for ${config.name} in 5 seconds`);
        setTimeout(() => {
          this.retryServiceInitialization(config).catch(retryError => {
            logger.error(`Retry failed for ${config.name}`, { error: retryError });
          });
        }, 5000);
      } else {
        // Non-recoverable optional service failure
        logger.warn(`⚠️ Optional service ${config.name} failed permanently`);
      }
    }
  }

  /**
   * Retry service initialization with exponential backoff and jitter
   */
  private async retryServiceInitialization(config: ServiceConfig): Promise<void> {
    const maxRetries = 3;
    let retryCount = 0;
    const status = this.statuses.get(config.name)!;

    while (retryCount < maxRetries && !status.initialized) {
      retryCount++;
      logger.info(`🔄 Retrying ${config.name} (attempt ${retryCount}/${maxRetries})`);
      
      const retryStart = Date.now();
      try {
        await this.initializeService(config);
        if (status.initialized) {
          logger.info(`✅ ${config.name} retry successful on attempt ${retryCount}`);
          return;
        }
      } catch (retryError) {
        const retryDuration = Date.now() - retryStart;
        logger.warn(`❌ Retry ${retryCount} failed for ${config.name}`, { 
          error: retryError instanceof Error ? retryError.message : String(retryError),
          duration: `${retryDuration}ms`
        });
        
        if (retryCount >= maxRetries) {
          logger.error(`💥 Max retries (${maxRetries}) exhausted for ${config.name}`);
          status.error = retryError instanceof Error ? retryError : new Error('Max retries exhausted');
          break;
        }
      }
      
      // Exponential backoff with jitter (1-30 seconds)
      const baseDelay = Math.min(1000 * Math.pow(2, retryCount), 30000);
      const jitter = Math.random() * baseDelay * 0.1; // 10% jitter
      const delay = baseDelay + jitter;
      
      logger.info(`⏳ Waiting ${Math.round(delay/1000)}s before retry ${retryCount + 1} for ${config.name}`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  /**
   * Topological sort for service dependencies with circular dependency detection
   */
  private sortServices(): ServiceConfig[] {
    const sorted: ServiceConfig[] = [];
    const visited = new Set<string>();
    const visiting = new Set<string>();
    const orphanedServices: string[] = [];

    const visit = (name: string): boolean => {
      if (visited.has(name)) return true;
      
      if (visiting.has(name)) {
        logger.error(`🔄 Circular dependency detected: ${name} (breaking cycle)`);
        visiting.delete(name); // Break the cycle
        return false;
      }

      visiting.add(name);
      const service = this.services.get(name);
      
      if (!service) {
        logger.warn(`Service not found during sorting: ${name}`);
        orphanedServices.push(name);
        visiting.delete(name);
        return false;
      }

      // Visit dependencies first (topological order)
      let allDepsResolved = true;
      if (service.dependencies) {
        for (const dep of service.dependencies) {
          if (this.services.has(dep)) {
            if (!visit(dep)) {
              allDepsResolved = false;
            }
          } else {
            logger.warn(`Missing dependency ${dep} for service ${name} - treating as resolved`);
          }
        }
      }
      
      // Only add to sorted list if all dependencies resolved
      if (allDepsResolved) {
        sorted.push(service);
        visited.add(name);
      }
      
      visiting.delete(name);
      return allDepsResolved;
    };

    // Visit all registered services
    for (const [name] of this.services) {
      if (!visited.has(name)) {
        visit(name);
      }
    }

    // Secondary sort by priority (higher first)
    sorted.sort((a, b) => (b.priority || 0) - (a.priority || 0));

    // Log sorting results
    const unresolved = Array.from(this.services.keys()).filter(name => !visited.has(name));
    if (unresolved.length > 0 || orphanedServices.length > 0) {
      logger.warn('Service sorting completed with issues', {
        unresolved,
        orphaned: orphanedServices,
        sorted: sorted.length,
        total: this.services.size
      });
    } else {
      logger.debug('Service topological sort completed successfully', { 
        order: sorted.map(s => s.name),
        total: sorted.length 
      });
    }

    return sorted;
  }

  /**
   * Start health monitoring for successfully initialized services only
   */
  private startHealthMonitoring(): void {
    // Only start if we have health-checkable, initialized services
    const healthCheckableServices = Array.from(this.services.values())
      .filter(s => s.healthCheck && typeof s.healthCheck === 'function')
      .filter(s => {
        const status = this.statuses.get(s.name);
        return status?.initialized === true;
      });
    
    if (healthCheckableServices.length === 0) {
      logger.info('No health-checkable services available - health monitoring disabled');
      return;
    }

    this.healthCheckTimer = setInterval(async () => {
      logger.debug(`Running health check cycle (${healthCheckableServices.length} services)`);
      
      let healthyCount = 0;
      let unhealthyCount = 0;
      const cycleReport: Record<string, any> = {
        timestamp: new Date().toISOString(),
        total: healthCheckableServices.length,
        results: {}
      };
      
      for (const [name, config] of this.services) {
        if (!config.healthCheck) continue;
        
        const status = this.statuses.get(name);
        if (!status?.initialized) {
          cycleReport.results[name] = { status: 'not_initialized' };
          continue;
        }
        
        try {
          const isHealthy = await config.healthCheck();
          status.healthy = isHealthy;
          
          cycleReport.results[name] = {
            status: isHealthy ? 'healthy' : 'unhealthy',
            timestamp: new Date().toISOString(),
            required: config.required
          };
          
          if (isHealthy) {
            healthyCount++;
          } else {
            unhealthyCount++;
            
            if (config.required) {
              logger.warn(`🚨 Critical service unhealthy: ${name} - attempting recovery`);
              await this.recoverService(config).catch(recoveryError => {
                logger.error(`Recovery failed for critical service ${name}`, { 
                  error: recoveryError instanceof Error ? recoveryError.message : String(recoveryError) 
                });
              });
            } else {
              logger.warn(`⚠️ Non-critical service unhealthy: ${name}`);
            }
          }
        } catch (healthError) {
          unhealthyCount++;
          status.healthy = false;
          
          cycleReport.results[name] = {
            status: 'check_failed',
            error: healthError instanceof Error ? healthError.message : String(healthError),
            timestamp: new Date().toISOString()
          };
          
          logger.error(`Health check failed for ${name}`, { 
            error: healthError instanceof Error ? healthError.message : String(healthError) 
          });
        }
      }
      
      // Log cycle summary
      logger.info('Health check cycle completed', {
        healthy: healthyCount,
        unhealthy: unhealthyCount,
        total: healthCheckableServices.length
      });
      
      // Alert if critical services are down
      if (unhealthyCount > 0) {
        const criticalUnhealthy = healthCheckableServices
          .filter(s => s.required && !this.statuses.get(s.name)?.healthy)
          .map(s => s.name);
        
        if (criticalUnhealthy.length > 0) {
          toast.warning(`Critical services degraded: ${criticalUnhealthy.join(', ')}`);
        }
      }
      
    }, 30000); // Check every 30 seconds

    logger.info(`Health monitoring started for ${healthCheckableServices.length} services`);
  }

  /**
   * Attempt to recover a failed or unhealthy service
   */
  private async recoverService(config: ServiceConfig): Promise<void> {
    const status = this.statuses.get(config.name)!;
    logger.info(`🔄 Attempting recovery for ${config.name}`, { 
      required: config.required,
      currentState: status.initialized ? 'unhealthy' : 'failed' 
    });
    
    const recoveryStart = Date.now();
    try {
      // Step 1: Cleanup existing state
      if (config.cleanup && typeof config.cleanup === 'function') {
        await config.cleanup().catch(cleanupError => {
          logger.warn(`Cleanup failed during recovery for ${config.name}`, { 
            error: cleanupError instanceof Error ? cleanupError.message : String(cleanupError) 
          });
        });
      }
      
      // Step 2: Reset status
      status.initialized = false;
      status.error = undefined;
      status.healthy = undefined;
      status.startTime = new Date();
      
      // Step 3: Re-initialize
      await this.initializeService(config);
      
      // Step 4: Verify health if possible
      if (config.healthCheck && typeof config.healthCheck === 'function') {
        const isHealthy = await config.healthCheck();
        status.healthy = isHealthy;
        
        if (isHealthy) {
          const recoveryDuration = Date.now() - recoveryStart;
          logger.info(`✅ ${config.name} recovered successfully`, { 
            duration: `${recoveryDuration}ms`,
            required: config.required 
          });
        } else {
          logger.warn(`⚠️ ${config.name} re-initialized but still unhealthy`);
        }
      } else {
        logger.info(`✅ ${config.name} re-initialized (no health check available)`);
      }
    } catch (recoveryError) {
      const recoveryDuration = Date.now() - recoveryStart;
      status.error = recoveryError instanceof Error ? recoveryError : new Error(String(recoveryError));
      status.endTime = new Date();
      status.duration = recoveryDuration;
      status.healthy = false;
      
      logger.error(`💥 Recovery failed for ${config.name}`, { 
        error: status.error.message,
        duration: `${recoveryDuration}ms`,
        required: config.required 
      });
      
      if (config.required) {
        toast.error(`Critical service ${config.name} failed to recover. Please refresh the application.`);
      }
    }
  }

  /**
   * Handle user sign out with comprehensive cleanup
   */
  private async handleSignOut(): Promise<void> {
    logger.info('👋 Handling user sign out - performing cleanup');
    
    try {
      // Step 1: Clear all active subscriptions
      await subscriptionManager.unsubscribeAll().catch(subError => {
        logger.warn('Subscription cleanup failed during sign out', { error: subError });
      });
      
      // Step 2: Clear API cache
      if (typeof APIClient.clearCache === 'function') {
        APIClient.clearCache();
        logger.debug('API cache cleared on sign out');
      }
      
      // Step 3: Cleanup services
      await this.cleanup();
      
      // Step 4: Reset state
      this.isInitialized = false;
      this.statuses.clear();
      
      // Step 5: Clear local storage (optional - respect user preferences)
      const shouldClearStorage = confirm('Clear application data? (recommended for security)');
      if (shouldClearStorage) {
        localStorage.clear();
        sessionStorage.clear();
        logger.info('Local storage cleared on sign out');
      }
      
      // Step 6: Redirect to login
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    } catch (signoutError) {
      logger.error('Sign out cleanup error', { error: signoutError });
      // Force redirect even if cleanup fails
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    }
  }

  /**
   * Setup comprehensive shutdown handlers
   */
  private setupShutdownHandlers(): void {
    if (typeof window === 'undefined') {
      logger.debug('Shutdown handlers not applicable in non-browser environment');
      return;
    }

    let cleanupTimeout: NodeJS.Timeout | null = null;

    // Handle page unload/beforeunload
    const handleBeforeUnload = async (e: BeforeUnloadEvent) => {
      if (this.isInitialized && !e.defaultPrevented) {
        e.preventDefault();
        logger.info('Page unloading - performing emergency cleanup');
        
        // Quick cleanup with timeout
        cleanupTimeout = setTimeout(async () => {
          try {
            await this.cleanup(true); // Quick cleanup
            logger.debug('Emergency cleanup completed');
          } catch (error) {
            logger.warn('Emergency cleanup failed', { error });
          }
        }, 0);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handleBeforeUnload);

    // Handle visibility change for background sync
    const handleVisibilityChange = () => {
      if (document.hidden) {
        logger.debug('Page hidden - pausing non-critical timers');
        // Pause non-critical operations
      } else {
        logger.debug('Page visible - resuming operations');
        
        // Resume and check health (debounced to avoid rapid calls)
        if (this.isInitialized) {
          const healthCheck = () => this.checkServiceHealth();
          const debouncedHealthCheck = () => {
            if (cleanupTimeout) clearTimeout(cleanupTimeout);
            cleanupTimeout = setTimeout(healthCheck, 1500);
          };
          debouncedHealthCheck();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Handle network connectivity changes
    const handleOnline = () => {
      logger.info('🌐 Network reconnected - performing health check');
      if (this.isInitialized) {
        this.checkServiceHealth().catch(error => {
          logger.warn('Health check failed after reconnect', { error });
        });
      }
    };

    const handleOffline = () => {
      logger.warn('🌐 Network disconnected - caching responses where possible');
      // Enable offline mode indicators
      if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('data-offline', 'true');
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup event listeners on ServiceInitializer reset
    (this as any).eventListeners = {
      beforeunload: handleBeforeUnload,
      visibilitychange: handleVisibilityChange,
      online: handleOnline,
      offline: handleOffline
    };

    logger.debug('Shutdown handlers configured');
  }

  /**
   * Perform comprehensive health check with detailed reporting
   */
  async checkServiceHealth(): Promise<Map<string, boolean>> {
    const healthResults = new Map<string, boolean>();
    const healthReport: {
      timestamp: string;
      totalServices: number;
      healthyServices: number;
      unhealthyServices: number;
      degradedServices: number;
      checks: Record<string, {
        status: 'healthy' | 'unhealthy' | 'degraded' | 'error' | 'not_initialized';
        timestamp: string;
        required?: boolean;
        error?: string;
        duration?: number;
      }>;
    } = {
      timestamp: new Date().toISOString(),
      totalServices: this.services.size,
      healthyServices: 0,
      unhealthyServices: 0,
      degradedServices: 0,
      checks: {}
    };

    const startTime = Date.now();
    
    for (const [name, config] of this.services) {
      const status = this.statuses.get(name);
      if (!status) {
        healthReport.checks[name] = { status: 'not_initialized', timestamp: new Date().toISOString() };
        continue;
      }

      if (!config.healthCheck || typeof config.healthCheck !== 'function') {
        healthResults.set(name, true);
        healthReport.checks[name] = { 
          status: 'no_check', 
          timestamp: new Date().toISOString(),
          required: config.required 
        };
        if (status.initialized) healthReport.healthyServices++;
        continue;
      }

      const checkStart = Date.now();
      try {
        if (!status.initialized) {
          healthResults.set(name, false);
          healthReport.checks[name] = { 
            status: 'not_initialized', 
            timestamp: new Date().toISOString(),
            required: config.required 
          };
          continue;
        }

        const isHealthy = await config.healthCheck();
        const checkDuration = Date.now() - checkStart;
        
        healthResults.set(name, isHealthy);
        status.healthy = isHealthy;

        const checkResult = {
          status: isHealthy ? 'healthy' : 'unhealthy',
          timestamp: new Date().toISOString(),
          required: config.required,
          duration: checkDuration
        };

        healthReport.checks[name] = checkResult;

        if (isHealthy) {
          healthReport.healthyServices++;
        } else {
          healthReport.unhealthyServices++;
          
          if (config.required) {
            logger.error(`🚨 CRITICAL: Required service ${name} is unhealthy`);
            // Attempt immediate recovery for critical services
            this.recoverService(config).catch(recoveryError => {
              logger.error(`Recovery attempt failed for critical service ${name}`, { 
                error: recoveryError instanceof Error ? recoveryError.message : String(recoveryError) 
              });
            });
          } else {
            logger.warn(`⚠️ Service ${name} is unhealthy`);
          }

          // Mark as degraded if it was previously healthy
          if (status.healthy === true) {
            healthReport.degradedServices++;
            status.healthy = 'degraded' as any;
          }
        }
      } catch (healthError) {
        const checkDuration = Date.now() - checkStart;
        healthResults.set(name, false);
        status.healthy = false;

        const errorMsg = healthError instanceof Error ? healthError.message : String(healthError);
        healthReport.checks[name] = {
          status: 'error',
          error: errorMsg,
          timestamp: new Date().toISOString(),
          required: config.required,
          duration: checkDuration
        };

        healthReport.unhealthyServices++;
        healthReport.degradedServices++;

        logger.error(`Health check failed for ${name}`, { 
          error: errorMsg,
          duration: `${checkDuration}ms`,
          required: config.required
        });
      }
    }

    const totalDuration = Date.now() - startTime;
    
    // Generate summary
    const summary = {
      ...healthReport,
      totalDuration: `${totalDuration}ms`,
      availability: healthReport.healthyServices / Math.max(1, healthReport.totalServices) * 100,
      criticalStatus: healthReport.unhealthyServices === 0 ? 'healthy' : 'degraded'
    };

    logger.info('Comprehensive health check completed', summary);

    // Alert users if critical degradation
    const criticalUnhealthy = Object.entries(healthReport.checks)
      .filter(([_, check]) => check.required && check.status !== 'healthy')
      .map(([name]) => name);

    if (criticalUnhealthy.length > 0) {
      const message = `Critical services degraded: ${criticalUnhealthy.join(', ')}. Some features may not work properly.`;
      logger.error(message);
      if (typeof toast !== 'undefined') {
        toast.error(message);
      }
    }

    return healthResults;
  }

  /**
   * Get list of failed service names
   */
  getFailedServices(): string[] {
    const failed: string[] = [];
    for (const [name, status] of this.statuses.entries()) {
      if (!status.initialized || status.error || !status.healthy) {
        failed.push(name);
      }
    }
    return failed;
  }

  getStatus(serviceName?: string): ServiceStatus | ServiceStatus[] | null {
    if (serviceName) {
      const status = this.statuses.get(serviceName);
      if (!status) {
        logger.warn(`Status requested for unknown service: ${serviceName}`);
        return null;
      }
      
      // Enhance with current health if available
      const config = this.services.get(serviceName);
      if (config?.healthCheck) {
        // Trigger immediate health check for this service
        config.healthCheck().then(isHealthy => {
          status.healthy = isHealthy;
        }).catch(error => {
          logger.warn(`On-demand health check failed for ${serviceName}`, { error });
          status.healthy = false;
        });
      }
      
      return { ...status };
    }
    
    const allStatuses = Array.from(this.statuses.values()).map(status => ({ ...status }));
    return allStatuses;
  }

  /**
   * Generate comprehensive initialization and health report
   */
  getInitializationReport(): {
    initialized: boolean;
    totalServices: number;
    successfulServices: number;
    failedServices: number;
    degradedServices: number;
    totalDuration: number;
    services: ServiceStatus[];
    healthSummary: {
      healthy: number;
      unhealthy: number;
      degraded: number;
      lastCheck?: string;
      availability: number;
      criticalStatus: 'healthy' | 'degraded' | 'critical';
    };
    recommendations?: string[];
  } {
    const services = Array.from(this.statuses.values());
    const successful = services.filter(s => s.initialized && !s.error && s.healthy !== false).length;
    const failed = services.filter(s => s.error).length;
    const degraded = services.filter(s => s.initialized && !s.error && s.healthy === false).length;
    const totalDuration = services.reduce((sum, s) => sum + (s.duration || 0), 0);

    // Calculate health summary
    const healthSummary = services.reduce((acc, s) => {
      if (typeof s.healthy === 'boolean') {
        if (s.healthy) acc.healthy++;
        else acc.unhealthy++;
      } else if (s.healthy === 'degraded') {
        acc.degraded++;
      }
      return acc;
    }, { healthy: 0, unhealthy: 0, degraded: 0 } as any);

    const totalHealthCheckable = healthSummary.healthy + healthSummary.unhealthy + healthSummary.degraded;
    const availability = totalHealthCheckable > 0 
      ? (healthSummary.healthy / totalHealthCheckable) * 100 
      : 100;

    const criticalStatus = failed > 0 ? 'critical' : degraded > 0 ? 'degraded' : 'healthy';

    const report = {
      initialized: this.isInitialized && failed === 0,
      totalServices: services.length,
      successfulServices: successful,
      failedServices: failed,
      degradedServices: degraded,
      totalDuration,
      services: services.map(s => ({ ...s })),
      healthSummary: {
        healthy: healthSummary.healthy,
        unhealthy: healthSummary.unhealthy,
        degraded: healthSummary.degraded,
        lastCheck: new Date().toISOString(),
        availability: Math.round(availability * 10) / 10, // 1 decimal place
        criticalStatus
      }
    };

    // Generate recommendations
    const recommendations: string[] = [];
    
    if (failed > 0) {
      recommendations.push('Review failed services and check logs for errors');
    }
    
    if (degraded > 0) {
      recommendations.push('Investigate degraded services - they may need configuration');
    }
    
    if (availability < 90) {
      recommendations.push('System availability below 90% - consider scaling resources');
    }
    
    if (recommendations.length === 0) {
      recommendations.push('System is healthy - no immediate action required');
    }

    report.recommendations = recommendations;
    
    logger.debug('Initialization report generated', report);
    return report;
  }

  /**
   * Perform complete cleanup with error isolation and logging
   */
  async cleanup(quick = false): Promise<void> {
    const cleanupStart = Date.now();
    logger.info('🧹 Starting service cleanup', { 
      quick,
      initializedServices: Array.from(this.statuses.values()).filter(s => s.initialized).length 
    });
    
    let cleanupErrors = 0;

    // Step 1: Stop all monitoring timers
    if (this.healthCheckTimer) {
      clearInterval(this.healthCheckTimer);
      this.healthCheckTimer = null;
      logger.debug('Health monitoring timer stopped');
    }

    if ((this as any).cacheCleanupTimer) {
      clearInterval((this as any).cacheCleanupTimer);
      delete (this as any).cacheCleanupTimer;
      logger.debug('Cache cleanup timer stopped');
    }

    // Step 2: Cleanup services in reverse dependency order
    const sortedServices = this.sortServices().reverse();
    const cleanupPromises: Promise<void>[] = [];
    
    for (const service of sortedServices) {
      const status = this.statuses.get(service.name);
      
      if (status?.initialized && service.cleanup && typeof service.cleanup === 'function') {
        const serviceCleanup = async () => {
          try {
            await service.cleanup();
            logger.debug(`Cleanup completed for ${service.name}`);
          } catch (cleanupError) {
            cleanupErrors++;
            const errorMsg = cleanupError instanceof Error ? cleanupError.message : String(cleanupError);
            logger.error(`Cleanup failed for ${service.name}`, { 
              error: errorMsg,
              required: service.required 
            });
            
            if (service.required) {
              // Critical service cleanup failure - log prominently
              console.error(`CRITICAL: Failed to cleanup required service ${service.name}:`, cleanupError);
            }
          }
        };

        if (quick) {
          // Fire and forget for quick cleanup
          cleanupPromises.push(serviceCleanup());
        } else {
          // Sequential cleanup for thorough cleanup
          await serviceCleanup();
        }
      }
    }

    // Wait for all parallel cleanups if needed
    if (!quick && cleanupPromises.length > 0) {
      const results = await Promise.allSettled(cleanupPromises);
      const failedCleanups = results.filter(r => r.status === 'rejected').length;
      if (failedCleanups > 0) {
        logger.warn(`${failedCleanups} cleanup operations failed`);
      }
    }

    // Step 3: Execute shutdown handlers
    for (let i = this.shutdownHandlers.length - 1; i >= 0; i--) {
      const handler = this.shutdownHandlers[i];
      try {
        await handler();
        logger.debug(`Shutdown handler ${i + 1} executed`);
      } catch (handlerError) {
        cleanupErrors++;
        logger.error(`Shutdown handler ${i + 1} failed`, { 
          error: handlerError instanceof Error ? handlerError.message : String(handlerError) 
        });
      }
    }

    // Step 4: Clear global state
    this.statuses.clear();
    this.isInitialized = false;
    this.isInitializing = false;

    // Step 5: Final cleanup (cache, storage, etc.)
    try {
      if (typeof APIClient.clearCache === 'function') {
        APIClient.clearCache();
      }
    } catch (cacheError) {
      logger.warn('Final cache cleanup failed', { error: cacheError });
    }

    const totalDuration = Date.now() - cleanupStart;
    logger.info('🧹 Service cleanup completed', { 
      quick,
      duration: `${totalDuration}ms`,
      cleanupErrors,
      servicesProcessed: sortedServices.length 
    });

    if (cleanupErrors > 0) {
      logger.warn(`Cleanup completed with ${cleanupErrors} errors - check logs for details`);
    }
  }

  /**
   * Register shutdown handler with validation
   */
  onShutdown(handler: () => Promise<void>): void {
    if (typeof handler !== 'function') {
      logger.error('Invalid shutdown handler provided');
      return;
    }
    
    this.shutdownHandlers.push(handler);
    logger.debug('Shutdown handler registered', { 
      totalHandlers: this.shutdownHandlers.length,
      handlerName: handler.name || 'anonymous' 
    });
  }

  /**
   * Reset ServiceInitializer instance (testing only)
   */
  static reset(): void {
    if (this.instance) {
      logger.info('Resetting ServiceInitializer instance');
      this.instance.cleanup().catch(resetError => {
        logger.warn('Error during ServiceInitializer reset', { error: resetError });
      });
      this.instance = null;
      logger.info('ServiceInitializer reset completed');
    } else {
      logger.debug('ServiceInitializer reset called but no instance exists');
    }
  }
}

// Export singleton instance with lazy initialization to avoid TDZ errors
let _serviceInitializer: ServiceInitializer | null = null;
const getServiceInitializer = (): ServiceInitializer => {
  if (!_serviceInitializer) {
    _serviceInitializer = ServiceInitializer.getInstance();
  }
  return _serviceInitializer;
};

// Use Proxy to forward all property access and method calls to the instance
export const serviceInitializer = new Proxy({} as ServiceInitializer, {
  get(_target, prop) {
    const instance = getServiceInitializer();
    const value = (instance as any)[prop];
    // If it's a function, bind it to the instance
    if (typeof value === 'function') {
      return value.bind(instance);
    }
    return value;
  },
  set(_target, prop, value) {
    const instance = getServiceInitializer();
    (instance as any)[prop] = value;
    return true;
  },
  has(_target, prop) {
    const instance = getServiceInitializer();
    return prop in instance;
  },
});

